import { useMemo, useCallback, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { DrawerProps } from '../Drawer';
import { Button } from '../../Button';
import { Label } from '../../Label';
import { Table } from 'antd';
import { ColumnsType } from 'antd/lib/table/interface';
import { Checkbox, Tag, Flex, Modal, message } from 'antd';
import { StyledViewSelectedListingProductsDrawer, StyledFooter } from './style';
import type {
  ListingApplication,
  Listing,
  SplitDeliveryData,
  SplitDeliveryProduct,
  ApplicationDeliveryCondition,
} from '../../../types/ListingData';
import type { TreeTableRow } from '../../../admin/pages/Listings/ListingApplicationsPage/types';
import type { ExtendedApplicationProductData } from '../../../admin/pages/Listings/ListingApplicationsPage/types';
import { DrawerSplitDeliveries } from './DrawerSplitDeliveries';
import { DrawerSendMessage } from './DrawerSendMessage';
import { useBreakpoint } from '../../../utils/useBreakpoint';
import { adminPath } from 'enums/adminRoutes';

interface ViewApplicationRecord {
  id: number | undefined;
  status: string | undefined;
  delivery_cost: number | undefined;
  total_offer_price: number | string | null | undefined;
  order_transaction:
    | { id: number; parent_transaction_id?: number | null }
    | null
    | undefined;
  openProductsTab: boolean;
}

interface ExtendedListingApplication extends ListingApplication {
  application_products?: ExtendedApplicationProductData[];
}

interface ExtendedTreeTableRow extends TreeTableRow {
  selected_quantity?: number;
}

interface DrawerViewSelectedListingProductsProps extends DrawerProps {
  selectedProducts?: Array<{
    listing_application_id: number;
    listing_application_product_id: number;
    selected_quantity: number;
  }>;
  savedSelectedProducts?: Array<{
    listing_application_id: number;
    listing_application_product_id: number;
    selected_quantity: number;
  }>;
  applications?: ExtendedListingApplication[];
  listing?: Listing | null;
  existingSplitDeliveries?: SplitDeliveryData[];
  onViewApplication?: (
    application:
      | ListingApplication
      | TreeTableRow
      | ViewApplicationRecord
      | null
  ) => void;
  selectProducts?: (data: {
    listing_id: string;
    selections: Array<{
      application_id: number;
      listing_application_product_id: number;
      selected_quantity: number;
    }>;
    split_deliveries?: SplitDeliveryData[];
  }) => void;
  onCreateTransactions?: (data: { listing_id: string }) => void;
  onRefetch?: () => void;
  onClose?: () => void;
}

const selectionHasChanges = (
  current: Array<{
    listing_application_id: number;
    listing_application_product_id: number;
    selected_quantity: number;
  }>,
  saved: Array<{
    listing_application_id: number;
    listing_application_product_id: number;
    selected_quantity: number;
  }>
): boolean => {
  const toKey = (sp: {
    listing_application_id: number;
    listing_application_product_id: number;
    selected_quantity: number;
  }) =>
    `${sp.listing_application_id}:${sp.listing_application_product_id}:${sp.selected_quantity}`;
  const currentFiltered = current.filter((sp) => sp.selected_quantity > 0);
  const savedFiltered = saved.filter((sp) => sp.selected_quantity > 0);
  const currentKeys = currentFiltered.map(toKey);
  const savedSet = new Set(savedFiltered.map(toKey));
  if (currentKeys.length !== savedSet.size) return true;
  return currentKeys.some((key) => !savedSet.has(key));
};

const filterSplitDeliveriesToSelectedProducts = (
  splitDeliveries: SplitDeliveryData[],
  selectedProducts: Array<{
    listing_application_id: number;
    listing_application_product_id: number;
    selected_quantity: number;
  }>,
  availableApplicationIds?: Set<number>
): SplitDeliveryData[] => {
  const selectedProductKeys = new Set(
    selectedProducts.map(
      (sp) =>
        `${sp.listing_application_id}:${sp.listing_application_product_id}`
    )
  );

  return splitDeliveries
    .map((delivery) => ({
      ...delivery,
      products: delivery.products.filter((product) => {
        const key = `${product.application_id}:${product.application_product_id}`;
        const inSelected = selectedProductKeys.has(key);
        const appAvailable =
          !availableApplicationIds ||
          availableApplicationIds.has(product.application_id);
        return inSelected && appAvailable;
      }),
    }))
    .filter((delivery) => delivery.products.length > 0);
};

const computeDeliveryForApplication = (
  application: ExtendedListingApplication,
  listing: Listing | null,
  selectedProducts: Array<{
    listing_application_id: number;
    listing_application_product_id: number;
    selected_quantity: number;
  }>
): SplitDeliveryData | null => {
  if (!listing) return null;

  const appSelectedProducts = selectedProducts.filter(
    (sp) => sp.listing_application_id === application.id
  );

  if (appSelectedProducts.length === 0) return null;

  let deliveryAddress = '';
  let deliveryStartDate: string | undefined;
  let deliveryEndDate: string | undefined;
  let deliveryFrequencyValue: number | undefined;
  let deliveryFrequencyUnit: string | undefined;

  const acceptedDeliveryCondition = application.delivery_conditions?.find(
    (dc: ApplicationDeliveryCondition) =>
      dc.is_accepting_all && dc.listing_delivery_condition_id
  );

  if (acceptedDeliveryCondition && listing.delivery_conditions) {
    const listingDeliveryCondition = listing.delivery_conditions.find(
      (ldc) =>
        ldc.id === acceptedDeliveryCondition.listing_delivery_condition_id
    );

    if (listingDeliveryCondition) {
      deliveryAddress = listingDeliveryCondition.delivery_address || '';
      deliveryStartDate = listingDeliveryCondition.delivery_start_date;
      deliveryEndDate = listingDeliveryCondition.delivery_end_date;
      deliveryFrequencyValue =
        listingDeliveryCondition.delivery_frequency_value;
      deliveryFrequencyUnit = listingDeliveryCondition.delivery_frequency_unit;
    }
  }

  if (!deliveryAddress && application.delivery_conditions?.[0]) {
    const firstDelivery = application.delivery_conditions[0];
    deliveryAddress = firstDelivery.delivery_address || '';
    deliveryStartDate = firstDelivery.delivery_start_date;
    deliveryEndDate = firstDelivery.delivery_end_date;
    deliveryFrequencyValue = firstDelivery.delivery_frequency_value;
    deliveryFrequencyUnit = firstDelivery.delivery_frequency_unit;
  }

  if (!deliveryAddress && listing.delivery_conditions?.[0]) {
    const firstListingDelivery = listing.delivery_conditions[0];
    deliveryAddress = firstListingDelivery.delivery_address || '';
    deliveryStartDate = firstListingDelivery.delivery_start_date;
    deliveryEndDate = firstListingDelivery.delivery_end_date;
    deliveryFrequencyValue = firstListingDelivery.delivery_frequency_value;
    deliveryFrequencyUnit = firstListingDelivery.delivery_frequency_unit;
  }

  if (!deliveryAddress) {
    deliveryAddress = application.warehouse_address || '';
  }

  if (!deliveryStartDate || !deliveryEndDate) {
    const listingContractStart = listing.contract_terms?.contract_start_date;
    const listingContractEnd = listing.contract_terms?.contract_end_date;
    const listingPublicationStart = listing.publication_start_date;
    const listingPublicationEnd = listing.publication_end_date;
    const appOfferFrom = application.offer_valid_from;
    const appOfferUntil = application.offer_valid_until;

    if (!deliveryStartDate) {
      deliveryStartDate =
        listingContractStart ||
        listingPublicationStart ||
        appOfferFrom ||
        undefined;
    }
    if (!deliveryEndDate) {
      deliveryEndDate =
        listingContractEnd ||
        listingPublicationEnd ||
        appOfferUntil ||
        undefined;
    }
  }

  const products: SplitDeliveryProduct[] = [];
  appSelectedProducts.forEach((selected) => {
    const appProduct = application.application_products?.find(
      (ap) => ap.id === selected.listing_application_product_id
    );

    if (appProduct) {
      products.push({
        application_id: application.id,
        application_product_id: selected.listing_application_product_id,
        quantity: selected.selected_quantity,
        offered_price: appProduct.offered_price,
      });
    }
  });

  if (products.length === 0) return null;

  return {
    id: `delivery-${application.id}`,
    delivery_address: deliveryAddress,
    delivery_start_date: deliveryStartDate,
    delivery_end_date: deliveryEndDate,
    delivery_frequency_value: deliveryFrequencyValue,
    delivery_frequency_unit: deliveryFrequencyUnit,
    products,
  };
};

const computeInitialSplitDeliveriesFromApplications = (
  applications: ExtendedListingApplication[],
  listing: Listing | null,
  selectedProducts: Array<{
    listing_application_id: number;
    listing_application_product_id: number;
    selected_quantity: number;
  }>
): SplitDeliveryData[] => {
  if (!listing || !applications.length || !selectedProducts.length) {
    return [];
  }

  const eligibleApplications = applications.filter((app) => {
    const hasSelectedProducts = selectedProducts.some(
      (sp) => sp.listing_application_id === app.id
    );
    return (
      (app.status === 'submitted' ||
        app.status === 'selected' ||
        app.status === 'accepted') &&
      !app.order_transaction &&
      hasSelectedProducts
    );
  });

  const deliveries: SplitDeliveryData[] = [];

  eligibleApplications.forEach((application) => {
    const delivery = computeDeliveryForApplication(
      application,
      listing,
      selectedProducts
    );
    if (delivery) {
      deliveries.push(delivery);
    }
  });

  return deliveries;
};

export const DrawerViewSelectedListingProducts = ({
  open,
  onClose,
  closable = true,
  selectedProducts = [],
  savedSelectedProducts = [],
  applications = [],
  listing,
  existingSplitDeliveries = [],
  onViewApplication,
  selectProducts,
  onCreateTransactions,
  onRefetch,
}: DrawerViewSelectedListingProductsProps) => {
  const intl = useIntl();
  const { isLgUp } = useBreakpoint();
  const navigate = useNavigate();

  const [showSplitDeliveriesDrawer, setShowSplitDeliveriesDrawer] =
    useState(false);
  const [showSendMessageDrawer, setShowSendMessageDrawer] = useState(false);
  const [splitDeliveries, setSplitDeliveries] = useState<SplitDeliveryData[]>(
    []
  );
  const [hasLocalEdits, setHasLocalEdits] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const availableApplications = useMemo(() => {
    const idsWithSelectedProducts = new Set(
      selectedProducts
        .filter((sp) => sp.selected_quantity > 0)
        .map((sp) => sp.listing_application_id)
    );
    return applications.filter(
      (app) =>
        idsWithSelectedProducts.has(app.id) &&
        (app.status === 'submitted' ||
          app.status === 'selected' ||
          app.status === 'accepted') &&
        !app.order_transaction
    );
  }, [applications, selectedProducts]);
  const availableApplicationIds = useMemo(
    () => new Set(availableApplications.map((app) => app.id)),
    [availableApplications]
  );

  useEffect(() => {
    if (existingSplitDeliveries.length > 0) {
      setHasLocalEdits(false);
    }
  }, [existingSplitDeliveries]);

  useEffect(() => {
    if (hasLocalEdits) {
      setSplitDeliveries((current) => {
        if (current.length === 0) return current;
        return filterSplitDeliveriesToSelectedProducts(
          current,
          selectedProducts,
          availableApplicationIds
        );
      });
      return;
    }

    const existingFiltered = filterSplitDeliveriesToSelectedProducts(
      existingSplitDeliveries,
      selectedProducts,
      availableApplicationIds
    );

    const applicationIdsWithExistingDeliveries = new Set(
      existingFiltered.flatMap((d) => d.products.map((p) => p.application_id))
    );

    const eligibleApplications = applications.filter((app) => {
      const hasSelectedProducts = selectedProducts.some(
        (sp) => sp.listing_application_id === app.id
      );
      return availableApplicationIds.has(app.id) && hasSelectedProducts;
    });

    const deliveriesToUse: SplitDeliveryData[] = [...existingFiltered];

    eligibleApplications.forEach((application) => {
      if (!applicationIdsWithExistingDeliveries.has(application.id)) {
        const delivery = computeDeliveryForApplication(
          application,
          listing || null,
          selectedProducts
        );
        if (delivery) {
          deliveriesToUse.push(delivery);
        }
      }
    });

    setSplitDeliveries(deliveriesToUse);
  }, [
    existingSplitDeliveries,
    applications,
    listing,
    selectedProducts,
    hasLocalEdits,
    availableApplicationIds,
  ]);

  const availableProducts = selectedProducts.filter(
    (selected) =>
      selected.selected_quantity > 0 &&
      availableApplicationIds.has(selected.listing_application_id)
  );

  const showSelectProducersButton = useMemo(() => {
    const hasChanges = selectionHasChanges(
      selectedProducts,
      savedSelectedProducts
    );
    if (!hasChanges) return false;

    const applicationsForSelection = applications.filter(
      (app) =>
        (app.status === 'submitted' ||
          app.status === 'accepted' ||
          app.status === 'selected') &&
        !app.order_transaction
    );
    const allSelectableProductKeys = applicationsForSelection.flatMap((app) =>
      (app.application_products || []).map((p) => `${app.id}:${p.id}`)
    );
    if (allSelectableProductKeys.length === 0) return false;

    const selectedProductKeysSet = new Set(
      selectedProducts
        .filter((sp) => sp.selected_quantity > 0)
        .map(
          (sp) =>
            `${sp.listing_application_id}:${sp.listing_application_product_id}`
        )
    );
    const hasUnselectedSelectable = allSelectableProductKeys.some(
      (key) => !selectedProductKeysSet.has(key)
    );
    const hasSelectedAll =
      selectedProductKeysSet.size >= allSelectableProductKeys.length;
    const hasRelevantSelection =
      selectedProducts.length > 0 || savedSelectedProducts.length > 0;
    return hasRelevantSelection && (hasUnselectedSelectable || hasSelectedAll);
  }, [applications, selectedProducts, savedSelectedProducts]);

  const eligibleApplications = applications.filter((app) => {
    const hasSelectedProducts = selectedProducts.some(
      (sp) => sp.listing_application_id === app.id
    );
    return (
      app.status === 'accepted' && !app.order_transaction && hasSelectedProducts
    );
  });

  const transactionLink = useMemo(() => {
    for (const app of applications) {
      if (app.order_transaction?.parent_transaction_id) {
        return `?motherTransaction=${app.order_transaction.parent_transaction_id}`;
      }
    }
    for (const app of applications) {
      if (app.order_transaction?.id) {
        return `?transaction=${app.order_transaction.id}`;
      }
    }
    return null;
  }, [applications]);

  const treeData = useMemo(() => {
    const categoryMap = new Map<string, TreeTableRow>();

    applications.forEach((application) => {
      application.application_products?.forEach(
        (product: ExtendedApplicationProductData) => {
          const categoryName =
            product.listing_product?.category?.name ||
            product.listing_product?.name ||
            '';
          const categoryId = `category-${(product.listing_product?.category?.id ?? categoryName) || product.listing_product_id}`;

          const selectedProduct = selectedProducts.find(
            (selected) =>
              selected.listing_application_id === application.id &&
              selected.listing_application_product_id === product.id
          );

          if (!selectedProduct) return;

          if (!categoryMap.has(categoryId)) {
            categoryMap.set(categoryId, {
              id: categoryId,
              category_name: categoryName,
              isCategoryRow: true,
              children: [],
            } as TreeTableRow);
          }

          const categoryRow = categoryMap.get(categoryId)!;
          if (!categoryRow.children) categoryRow.children = [];

          categoryRow.children.push({
            ...product,
            id: `product-${application.id}-${product.id}`,
            isProductRow: true,
            product_name: product.listing_product?.name || categoryName,
            offered_quantity: product.offered_quantity,
            offered_price: product.offered_price || 0,
            listing_product: product.listing_product,
            farm_name: application.farm_name,
            farm_brand: application.farm_brand,
            application_id: application.id,
            listing_product_id: product.listing_product_id,
            selected_quantity: selectedProduct.selected_quantity,
            status: application.status,
            delivery_cost: application.delivery_cost,
            calculated_distance_km: application.calculated_distance_km,
            total_offer_price: application.total_offer_price,
            order_transaction: application.order_transaction,
            listing_id: application.listing_id,
            applicant_farm_id: application.applicant_farm_id,
            submitted_at: application.submitted_at,
            isSelected: true,
          } as TreeTableRow);
        }
      );
    });

    return Array.from(categoryMap.values());
  }, [applications, selectedProducts]);

  useEffect(() => {
    if (treeData.length > 0) {
      setExpandedRowKeys(treeData.map((cat) => cat.id));
    }
  }, [treeData]);

  const totalPrice = useMemo(() => {
    return selectedProducts.reduce((total, selected) => {
      const app = applications.find(
        (a) => a.id === selected.listing_application_id
      );
      const appProduct = app?.application_products?.find(
        (ap) => ap.id === selected.listing_application_product_id
      );

      if (appProduct) {
        const price = Number(appProduct.offered_price || 0);
        return total + selected.selected_quantity * price;
      }

      return total;
    }, 0);
  }, [selectedProducts, applications]);

  const totalDeliveryCost = useMemo(() => {
    const appIds = new Set(
      selectedProducts.map((sp) => sp.listing_application_id)
    );
    return Array.from(appIds).reduce((total, appId) => {
      const app = applications.find((a) => a.id === appId);
      return total + Number(app?.delivery_cost ?? 0);
    }, 0);
  }, [selectedProducts, applications]);

  const handleSelectProducers = () => {
    if (!listing?.id || !selectProducts) {
      message.error(intl.formatMessage({ id: 'message.error' }));
      return;
    }

    const selections = selectedProducts.map((selected) => ({
      application_id: selected.listing_application_id,
      listing_application_product_id: selected.listing_application_product_id,
      selected_quantity: selected.selected_quantity,
    }));

    let hasExceedingQuantity = false;
    if (listing.products) {
      const selectedQuantitiesByProduct: { [key: string]: number } = {};

      selections.forEach((selection) => {
        const app = applications.find((a) => a.id === selection.application_id);
        const appProduct = app?.application_products?.find(
          (ap) => ap.id === selection.listing_application_product_id
        );
        const listingProductId = appProduct?.listing_product_id;

        if (listingProductId) {
          selectedQuantitiesByProduct[listingProductId] =
            (selectedQuantitiesByProduct[listingProductId] || 0) +
            selection.selected_quantity;
        }
      });

      hasExceedingQuantity = Object.entries(selectedQuantitiesByProduct).some(
        ([productId, selectedQuantity]) => {
          const listingProduct = listing.products?.find(
            (product) => product.id === productId
          );
          return (
            listingProduct &&
            selectedQuantity > (listingProduct.required_quantity || 0)
          );
        }
      );
    }

    const makeRequest = () => {
      selectProducts({
        listing_id: listing.id,
        selections,
        split_deliveries: splitDeliveries,
      });
    };

    if (hasExceedingQuantity) {
      Modal.confirm({
        title: intl.formatMessage({ id: 'general.confirm' }),
        content: intl.formatMessage({
          id: 'listings.applications.confirm_exceed_quantity',
        }),
        okText: intl.formatMessage({ id: 'general.yes' }),
        cancelText: intl.formatMessage({ id: 'general.no' }),
        onOk: makeRequest,
      });
    } else {
      makeRequest();
    }
  };

  const handleSplitDeliveriesClick = useCallback(() => {
    if (availableProducts.length === 0) {
      message.warning(
        intl.formatMessage({
          id: 'listings.applications.split_deliveries.no_available_products',
        })
      );
      return;
    }

    if (
      splitDeliveries.length === 0 &&
      applications.length > 0 &&
      selectedProducts.length > 0
    ) {
      const computed = computeInitialSplitDeliveriesFromApplications(
        applications,
        listing || null,
        selectedProducts
      );
      const filtered = filterSplitDeliveriesToSelectedProducts(
        computed,
        selectedProducts,
        availableApplicationIds
      );
      setSplitDeliveries(filtered);
    }

    setShowSplitDeliveriesDrawer(true);
  }, [
    availableProducts.length,
    intl,
    splitDeliveries,
    applications,
    listing,
    selectedProducts,
    availableApplicationIds,
  ]);

  const handleSplitDeliveriesSave = useCallback(
    (deliveries: SplitDeliveryData[]) => {
      setHasLocalEdits(true);
      setSplitDeliveries(deliveries);
      setShowSplitDeliveriesDrawer(false);
    },
    []
  );

  const handleCreateTransactions = useCallback(async () => {
    if (!listing?.id || !onCreateTransactions) {
      message.error(intl.formatMessage({ id: 'message.error' }));
      return;
    }

    try {
      await onCreateTransactions({
        listing_id: listing.id,
      });

      message.success(
        intl.formatMessage({
          id: 'listings.applications.transactions_created',
        })
      );

      onRefetch?.();
      onClose?.();
    } catch (error) {}
  }, [listing?.id, onCreateTransactions, intl, onRefetch, onClose]);

  const CustomTitle = () => (
    <Flex justify="space-between" align="center" className="title-container">
      <Label
        title={true}
        label={intl.formatMessage({
          id: 'listings.applications.view_selected.title',
        })}
      />
      {showSelectProducersButton && (
        <Button
          label={intl.formatMessage({
            id: 'listings.applications.view_selected.split_deliveries',
          })}
          onClick={handleSplitDeliveriesClick}
        />
      )}
    </Flex>
  );

  const Footer = () => (
    <StyledFooter>
      <div className="total-label left">
        <span>
          {intl.formatMessage({
            id: 'listings.applications.table.total_price',
          })}
          : {totalPrice.toFixed(2)} €
        </span>
        <span>
          {intl.formatMessage({ id: 'delivery.delivery' })}:{' '}
          {totalDeliveryCost.toFixed(2)} €
        </span>
      </div>
      <div className="footer-buttons">
        <Button
          label={intl.formatMessage({ id: 'general.back' })}
          onClick={onClose}
        />
        <Button
          label={intl.formatMessage({
            id: 'listings.applications.send_messages',
          })}
          onClick={() => setShowSendMessageDrawer(true)}
        />
        {showSelectProducersButton && (
          <Button
            label={intl.formatMessage({
              id: 'listings.applications.select_producers',
            })}
            type="primary"
            onClick={handleSelectProducers}
            disabled={
              listing?.status !== 'closed' &&
              listing?.status !== 'partially_completed'
            }
            className="title-button"
          />
        )}
        {eligibleApplications.length > 0 && (
          <Button
            label={intl.formatMessage({
              id: 'listings.applications.create_transaction',
            })}
            type="primary"
            onClick={handleCreateTransactions}
            disabled={eligibleApplications.length === 0}
          />
        )}
        {transactionLink && (
          <Button
            label={intl.formatMessage({
              id: 'listings.applications.open_mother_transaction',
            })}
            type="primary"
            onClick={() =>
              navigate(`${adminPath.farmTransactions()}${transactionLink}`)
            }
          />
        )}
      </div>
    </StyledFooter>
  );

  const columns: ColumnsType<TreeTableRow> = useMemo(
    () => [
      {
        title: intl.formatMessage({
          id: 'listings.applications.table.category',
        }),
        key: 'category',
        width: '20%',
        render: (record: TreeTableRow) => {
          if (record.isProductRow) {
            return record.farm_brand || record.farm_name || '';
          }

          return record.category_name || '';
        },
      },
      {
        title: intl.formatMessage({
          id: 'listings.applications.table.total_price',
        }),
        key: 'total_price',
        width: '18%',
        render: (record: ExtendedTreeTableRow) => {
          if (record.isProductRow) {
            const quantity = record.selected_quantity || 0;
            const price = record.offered_price || 0;
            const totalPrice = quantity * price;
            const unitPrice = price;
            const unit = record.listing_product?.storage_unit?.short || '';

            return (
              <div>
                {totalPrice > 0 && (
                  <>
                    <div>{totalPrice.toFixed(2)} €</div>
                    {unitPrice > 0 && unit && (
                      <div>
                        {unitPrice.toFixed(2)} €/{unit}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          }
          const totalCategoryPrice = (record.children || [])
            .filter((child: ExtendedTreeTableRow) => !child.isCategoryRow)
            .reduce((sum: number, child: ExtendedTreeTableRow) => {
              const quantity = child.selected_quantity || 0;
              const price = child.offered_price || 0;
              return sum + quantity * price;
            }, 0);

          return totalCategoryPrice > 0
            ? `${totalCategoryPrice.toFixed(2)} €`
            : '';
        },
      },
      {
        title: intl.formatMessage({
          id: 'listings.applications.table.quantity',
        }),
        key: 'quantity',
        width: '15%',
        render: (record: ExtendedTreeTableRow) => {
          if (record.isProductRow) {
            const quantity = record.selected_quantity || 0;
            const unit = record.listing_product?.storage_unit?.short || '';

            return quantity > 0 ? `${quantity} ${unit}` : '';
          }

          const categoryChildren = (record.children || []).filter(
            (child: ExtendedTreeTableRow) => !child.isCategoryRow
          );

          const totalQuantity = categoryChildren.reduce(
            (sum: number, child: ExtendedTreeTableRow) =>
              sum + (child.selected_quantity || 0),
            0
          );

          const firstChild =
            categoryChildren.length > 0 ? categoryChildren[0] : null;
          const unit = firstChild?.listing_product?.storage_unit?.short || '';

          return totalQuantity > 0 ? `${totalQuantity} ${unit}` : '';
        },
      },
      {
        title: intl.formatMessage({
          id: 'listings.applications.table.delivery_cost',
        }),
        key: 'delivery_cost',
        width: '18%',
        render: (record: ExtendedTreeTableRow) => {
          if (record.isProductRow) {
            const deliveryCost = Number(record.delivery_cost) || 0;
            return deliveryCost > 0 ? `${deliveryCost.toFixed(2)} €` : '';
          }

          const categoryChildren = (record.children || []).filter(
            (child: ExtendedTreeTableRow) => !child.isCategoryRow
          );

          const uniqueDeliveryCosts = new Map<number, number>();
          categoryChildren.forEach((child: ExtendedTreeTableRow) => {
            if (child.application_id && child.delivery_cost !== undefined) {
              const appId = child.application_id;
              const deliveryCost = Number(child.delivery_cost) || 0;
              if (!uniqueDeliveryCosts.has(appId)) {
                uniqueDeliveryCosts.set(appId, deliveryCost);
              }
            }
          });

          const totalDeliveryCost = Array.from(
            uniqueDeliveryCosts.values()
          ).reduce((sum, cost) => sum + cost, 0);

          return totalDeliveryCost > 0
            ? `${totalDeliveryCost.toFixed(2)} €`
            : '';
        },
      },
      {
        title: 'Attālums',
        key: 'distance',
        width: '15%',
        render: (record: ExtendedTreeTableRow) => {
          if (record.isProductRow) {
            const distance =
              typeof record.calculated_distance_km === 'string'
                ? parseFloat(record.calculated_distance_km)
                : record.calculated_distance_km;
            return distance && distance > 0 ? `${distance.toFixed(2)} km` : '';
          }

          return '';
        },
      },
      {
        title: 'Darījums',
        key: 'darījums',
        width: '5%',
        render: (record: TreeTableRow) => {
          if (!record.isProductRow) {
            return '';
          }

          return (
            <Checkbox checked={!!record.order_transaction} disabled={true} />
          );
        },
      },
      {
        title: '',
        key: 'action',
        render: (record: TreeTableRow) => {
          if (!record.isProductRow) {
            return '';
          }

          const getStatusInfo = (status: string | undefined) => {
            switch (status) {
              case 'selected':
                return {
                  label: intl.formatMessage({
                    id: 'listings.applications.selected',
                  }),
                  className: 'selected',
                };
              case 'accepted':
                return {
                  label: intl.formatMessage({
                    id: 'listings.applications.accepted',
                  }),
                  className: 'accepted',
                };
              case 'rejected':
                return {
                  label: intl.formatMessage({
                    id: 'listings.applications.rejected',
                  }),
                  className: 'rejected',
                };
              default:
                return null;
            }
          };

          const statusInfo = getStatusInfo(record.status);

          return (
            <div className="action-cell">
              {statusInfo && (
                <Tag className={`status-tag status ${statusInfo.className}`}>
                  {statusInfo.label}
                </Tag>
              )}
              <Button
                className="view-button"
                label={
                  ['selected', 'accepted', 'rejected'].includes(
                    record.status || ''
                  )
                    ? intl.formatMessage({ id: 'listing.open' })
                    : intl.formatMessage({ id: 'listings.applications.view' })
                }
                onClick={() => {
                  const applicationRecord = {
                    id: record.application_id,
                    status: record.status,
                    delivery_cost: record.delivery_cost,
                    total_offer_price: record.total_offer_price,
                    order_transaction: record.order_transaction,
                    openProductsTab: true,
                  };
                  onViewApplication?.(applicationRecord);
                }}
                size="small"
              />
            </div>
          );
        },
      },
    ],
    [intl]
  );

  return (
    <StyledViewSelectedListingProductsDrawer
      open={open}
      onClose={onClose}
      closable={closable}
      title={<CustomTitle />}
      width={1000}
      zIndex={1100}
      footer={<Footer />}
    >
      {treeData.length === 0 ? (
        <div>
          {intl.formatMessage({
            id: 'listings.applications.no_selected_products',
          })}
        </div>
      ) : isLgUp ? (
        <div className="table-container">
          <Table
            columns={columns}
            dataSource={treeData}
            rowKey="id"
            pagination={false}
            size="small"
            rowClassName={(record) =>
              (record as TreeTableRow).isCategoryRow
                ? 'category-row'
                : 'supplier-row'
            }
            expandable={{
              expandedRowKeys,
              onExpandedRowsChange: (keys) =>
                setExpandedRowKeys(Array.from(keys) as React.Key[]),
              expandIcon: () => null,
            }}
          />
        </div>
      ) : (
        <div className="mobile-cards-container">
          {treeData.map((category) => {
            const categoryTotalPrice = (category.children || []).reduce(
              (sum: number, child: ExtendedTreeTableRow) => {
                const qty =
                  child.selected_quantity ?? child.offered_quantity ?? 0;
                const price = child.offered_price ?? 0;
                return sum + Number(qty) * Number(price);
              },
              0
            );
            const categoryTotalQty = (category.children || []).reduce(
              (sum: number, child: ExtendedTreeTableRow) => {
                return (
                  sum +
                  Number(child.selected_quantity ?? child.offered_quantity ?? 0)
                );
              },
              0
            );
            const unit =
              (category.children as ExtendedTreeTableRow[])?.[0]
                ?.listing_product?.storage_unit?.short || '';
            const uniqueDeliveryCosts = new Map<number, number>();
            (category.children || []).forEach(
              (child: ExtendedTreeTableRow) => {
                if (child.application_id && !uniqueDeliveryCosts.has(child.application_id)) {
                  uniqueDeliveryCosts.set(
                    child.application_id,
                    Number(child.delivery_cost ?? 0)
                  );
                }
              }
            );
            const totalDeliveryCost = Array.from(
              uniqueDeliveryCosts.values()
            ).reduce((sum, cost) => sum + cost, 0);

            return (
              <div key={category.id} className="mobile-product-card">
                <div className="mobile-card-row">
                  <div className="mobile-card-field">
                    <span className="mobile-card-label">
                      {intl.formatMessage({ id: 'general.products' })}
                    </span>
                    <span className="mobile-card-value bold">
                      {category.category_name || '-'}
                    </span>
                  </div>
                  <div className="mobile-card-field end">
                    <span className="mobile-card-label">
                      {intl.formatMessage({
                        id: 'listings.applications.table.total_price',
                      })}
                    </span>
                    <span className="mobile-card-value">
                      {categoryTotalPrice > 0
                        ? `${categoryTotalPrice.toFixed(2)} EUR`
                        : '-'}
                    </span>
                  </div>
                </div>
                <div className="mobile-card-row">
                  <div className="mobile-card-field">
                    <span className="mobile-card-label">
                      {intl.formatMessage({
                        id: 'listings.applications.table.quantity',
                      })}
                    </span>
                    <span className="mobile-card-value">
                      {categoryTotalQty > 0
                        ? `${categoryTotalQty} ${unit}`
                        : '-'}
                    </span>
                  </div>
                  <div className="mobile-card-field end">
                    <span className="mobile-card-label">
                      {intl.formatMessage({ id: 'delivery.delivery' })}
                    </span>
                    <span className="mobile-card-value">
                      {totalDeliveryCost > 0
                        ? `${totalDeliveryCost.toFixed(2)} EUR`
                        : '-'}
                    </span>
                  </div>
                </div>
                {(category.children || []).map(
                  (child: ExtendedTreeTableRow) => {
                    const qty =
                      child.selected_quantity ?? child.offered_quantity ?? 0;
                    const unitPrice = child.offered_price ?? 0;
                    const totalPrice = Number(qty) * Number(unitPrice);
                    const childUnit =
                      child.listing_product?.storage_unit?.short || '';
                    const distance =
                      typeof child.calculated_distance_km === 'string'
                        ? parseFloat(child.calculated_distance_km)
                        : child.calculated_distance_km;

                    return (
                      <div key={child.id} className="mobile-farm-offer">
                        <span className="mobile-card-value bold">
                          {child.farm_brand || child.farm_name || '-'}
                        </span>
                        <div className="mobile-card-row bordered">
                          <div className="mobile-card-field">
                            <span className="mobile-card-label">
                              {intl.formatMessage({
                                id: 'listings.applications.table.total_price',
                              })}
                            </span>
                            <span className="mobile-card-value">
                              {totalPrice > 0
                                ? `${totalPrice.toFixed(2)} € - ${Number(unitPrice).toFixed(2)} €/${childUnit}`
                                : '-'}
                            </span>
                          </div>
                          <div className="mobile-card-field end">
                            <span className="mobile-card-label">
                              {intl.formatMessage({
                                id: 'listings.applications.table.quantity',
                              })}
                            </span>
                            <span className="mobile-card-value">
                              {Number(qty) > 0 ? `${qty} ${childUnit}` : '-'}
                            </span>
                          </div>
                        </div>
                        <div className="mobile-card-row bordered">
                          <div className="mobile-card-field">
                            <span className="mobile-card-label">
                              {intl.formatMessage({ id: 'form.distance' })}
                            </span>
                            <span className="mobile-card-value">
                              {distance && !isNaN(distance)
                                ? `${distance} km`
                                : '-'}
                            </span>
                          </div>
                          <div className="mobile-card-field end">
                            <span className="mobile-card-label">
                              {intl.formatMessage({ id: 'delivery.delivery' })}
                            </span>
                            <span className="mobile-card-value">
                              {Number(child.delivery_cost) > 0
                                ? `${Number(child.delivery_cost).toFixed(2)} EUR`
                                : '-'}
                            </span>
                          </div>
                        </div>
                        <Button
                          className="view-button mobile-view-btn"
                          label={intl.formatMessage({
                            id: 'listings.applications.view',
                          })}
                          onClick={() => {
                            const applicationRecord = {
                              id: child.application_id,
                              status: child.status,
                              delivery_cost: child.delivery_cost,
                              total_offer_price: child.total_offer_price,
                              order_transaction: child.order_transaction,
                              openProductsTab: true,
                            };
                            onViewApplication?.(applicationRecord);
                          }}
                        />
                      </div>
                    );
                  }
                )}
              </div>
            );
          })}
        </div>
      )}

      {showSplitDeliveriesDrawer && (
        <DrawerSplitDeliveries
          open={showSplitDeliveriesDrawer}
          onClose={() => setShowSplitDeliveriesDrawer(false)}
          selectedProducts={availableProducts}
          applications={availableApplications}
          listing={listing}
          existingDeliveries={splitDeliveries}
          onSplitDeliveriesSave={handleSplitDeliveriesSave}
        />
      )}
      <DrawerSendMessage
        open={showSendMessageDrawer}
        onClose={() => setShowSendMessageDrawer(false)}
        context={{
          type: 'listing',
          listingId: listing?.id,
        }}
      />
    </StyledViewSelectedListingProductsDrawer>
  );
};
